import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LoaderCircle, Save } from "lucide-react";
import { useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";

// Espejo de la validación de servidor (factory/dto.go): nombre obligatorio ≤120,
// descripción ≤500.
const areaSchema = z.object({
  nombre: z.string().trim().min(1, "El nombre es obligatorio").max(120, "Máximo 120 caracteres"),
  descripcion: z.string().trim().max(500, "Máximo 500 caracteres"),
});

export type AreaFormValues = z.infer<typeof areaSchema>;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<AreaFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.code === "NOMBRE_DUPLICADO") {
      setError("nombre", { message: "Ya existe un área con ese nombre." });
      return;
    }
    if (err.code === "VALIDACION" && err.fields) {
      if (err.fields.nombre) {
        setError("nombre", { message: err.fields.nombre });
      }
      if (err.fields.descripcion) {
        setError("descripcion", { message: err.fields.descripcion });
      }
      return;
    }
    setFormError(err.message || "No se pudo guardar el área.");
    return;
  }
  setFormError("No se pudo guardar el área. Comprueba tu conexión.");
}

/**
 * AreaForm es el formulario reutilizable de alta/edición de áreas (react-hook-form +
 * zod). Mapea los errores del backend a errores por campo (nombre duplicado,
 * validación). El patrón —schema espejo, aplicarError, onSubmit que lanza— es el que
 * replican los formularios de los demás maestros (#28–#31).
 */
export function AreaForm({
  defaultValues,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  defaultValues: AreaFormValues;
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: AreaFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<AreaFormValues>({ resolver: zodResolver(areaSchema), defaultValues });

  const submit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      aplicarError(err, setError, setFormError);
    }
  });

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="nombre">Nombre</Label>
        <Input
          id="nombre"
          autoComplete="off"
          aria-invalid={errors.nombre ? true : undefined}
          {...register("nombre")}
        />
        <FieldError>{errors.nombre?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="descripcion">Descripción (opcional)</Label>
        <Textarea
          id="descripcion"
          aria-invalid={errors.descripcion ? true : undefined}
          {...register("descripcion")}
        />
        <FieldError>{errors.descripcion?.message}</FieldError>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-5 w-5" aria-hidden="true" />
          )}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
